import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { WaTemplate } from "@/types";

const field = "w-full rounded-2xl border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40";

export const WaTemplatesCard = () => {
  const { t } = usePrefs();
  const [list, setList] = useState<WaTemplate[] | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/venue/wa-templates").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  if (!list) return null;
  const set = (i: number, k: "title" | "body", v: string) => setList(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const add = () => setList([...list, { id: `t${Date.now()}`, title: "", body: "" }]);
  const save = async () => {
    setSaving(true);
    try {
      setList((await api.put("/venue/wa-templates", { templates: list.map((x) => ({ ...x, title: x.title.trim(), body: x.body.trim() })) })).data);
      toast.success(t("wat.saved"));
    } catch (e) {
      toast.error(errorText(e, t));
    }
    setSaving(false);
  };
  return (
    <div className="rounded-3xl border bg-card p-5 space-y-4" data-testid="wa-templates-card">
      <div>
        <p className="font-display text-lg font-bold inline-flex items-center gap-2"><MessageCircle className="h-5 w-5 text-[#25D366]" />{t("wat.title")}</p>
        <p className="text-xs text-muted-foreground mt-1">{t("wat.hint")}</p>
      </div>
      {list.map((x, i) => (
        <div key={x.id} className="rounded-2xl bg-muted/50 p-3 space-y-2" data-testid={`wa-template-${i}`}>
          <div className="flex gap-2">
            <Input data-testid={`wa-template-title-${i}`} value={x.title} maxLength={40} placeholder={t("wat.name")} onChange={(e: any) => set(i, "title", e.target.value)} className="rounded-2xl" />
            {list.length > 1 && (
              <button data-testid={`wa-template-delete-${i}`} onClick={() => setList(list.filter((_, j) => j !== i))} aria-label={t("wat.delete")}
                className="h-10 w-10 shrink-0 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center active:scale-95 transition-transform"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
          <textarea data-testid={`wa-template-body-${i}`} value={x.body} maxLength={1000} rows={3} onChange={(e) => set(i, "body", e.target.value)} className={field} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {list.length < 10 && (
          <button data-testid="wa-template-add" onClick={add} className="h-10 px-4 rounded-full bg-accent text-sm font-bold inline-flex items-center gap-1.5 active:scale-95 transition-transform"><Plus className="h-4 w-4" />{t("wat.add")}</button>
        )}
        <button data-testid="wa-templates-save" onClick={save} disabled={saving || list.some((x) => !x.title.trim() || x.body.trim().length < 3)}
          className="h-10 px-5 rounded-full bg-foreground text-background text-sm font-bold disabled:opacity-50 active:scale-95 transition-transform">{t("wat.save")}</button>
      </div>
    </div>
  );
};
